import { useEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';
import type { Game, TutorialTarget } from '@/game/Game';
import { useTaskCards } from '@/ui/hooks/useTaskCards';
import styles from './CoachSpotlight.module.css';

interface Step {
  /** Canvas targets (all shown), e.g. the three boxes of T05. */
  canvas?: TutorialTarget[];
  /** DOM targets, the first one on screen wins (a sheet step comes before the tab that opens it). */
  dom?: string[];
  /** T01: dim the room around the first box for the first seconds. */
  dim?: boolean;
}

/** What each FTUE step points at (ux-screens §5 table, "Highlight" column). */
const STEPS: Record<string, Step> = {
  T01: { canvas: [{ kind: 'box', boxDefId: 'box_01_sleep' }], dim: true },
  T02: { dom: ['[data-testid="bag-item-fur_bed_pine"]'] },
  T03: { dom: ['[data-tutorial-id="rotate"]'] },
  T04: { canvas: [{ kind: 'item', itemId: 'fur_bed_pine' }] },
  T05: {
    canvas: [
      { kind: 'box', boxDefId: 'box_02_work' },
      { kind: 'box', boxDefId: 'box_03_books' },
      { kind: 'box', boxDefId: 'box_04_kitchen' },
    ],
  },
  T06: { canvas: [{ kind: 'box', boxDefId: 'box_05_granny' }] },
  T08: {
    canvas: [{ kind: 'item', itemId: 'fur_bookshelf' }],
    dom: ['[data-testid="bag-item-dec_book_stack"]'],
  },
  T09: { dom: ['[data-testid="catalog-dec_desk_lamp"]', '[data-testid="nav-shop"]'] },
  T10: { dom: ['[data-tutorial-id="place-now"]'], canvas: [{ kind: 'item', itemId: 'fur_desk_pine' }] },
};

/** T01 dims the room only this long, then the spotlight never blocks anything (ux-screens §5). */
const DIM_MS = 10_000;
const TRACK_MS = 66;
/** Story, dialogue and mini games cover the room: no rings over them. */
const COVERED = '[data-testid="dialogue"], [data-testid="minigame"], [data-testid="unlock-modal"]';

interface Ring {
  x: number;
  y: number;
  w: number;
  h: number;
  round: boolean;
}

/**
 * FTUE highlights: soft pulsing rings around what the current tutorial step needs (canvas objects through
 * `game.tutorialAnchor`, DOM elements by selector). Purely visual: pointer events pass through.
 */
export function CoachSpotlight({ game, paused }: { game: Game; paused: boolean }) {
  const { cards, inTutorial } = useTaskCards();
  const dragging = useStore(game.placementUi, (s) => s.dragging !== null);
  const step = inTutorial ? STEPS[cards[0]?.taskId ?? ''] : undefined;
  const [rings, setRings] = useState<Ring[]>([]);
  const [dim, setDim] = useState(false);
  const startedRef = useRef(0);

  useEffect(() => {
    if (!step || paused || dragging) return;
    startedRef.current = performance.now();
    let last = '';
    const tick = () => {
      const next: Ring[] = [];
      if (!document.querySelector(COVERED)) {
        for (const sel of step.dom ?? []) {
          const el = document.querySelector(sel);
          const r = el?.getBoundingClientRect();
          if (r && r.width > 0 && r.height > 0) {
            next.push({ x: r.left - 6, y: r.top - 6, w: r.width + 12, h: r.height + 12, round: false });
            break;
          }
        }
        // A sheet step (e.g. the catalog card) replaces the canvas targets behind it.
        if (next.length === 0 || !step.dom?.some((s) => s.includes('catalog') || s.includes('place-now'))) {
          // A room spot behind an open sheet (phones) gets no ring drawn on top of the sheet.
          const sheets = [...document.querySelectorAll('[aria-modal="true"]')].map((el) =>
            el.getBoundingClientRect(),
          );
          for (const target of step.canvas ?? []) {
            const a = game.tutorialAnchor(target);
            const hidden =
              a && sheets.some((r) => a.x >= r.left && a.x <= r.right && a.y >= r.top && a.y <= r.bottom);
            if (a && !hidden) next.push({ x: a.x - a.r, y: a.y - a.r, w: a.r * 2, h: a.r * 2, round: true });
          }
        }
      }
      const key = next.map((r) => `${r.x.toFixed(0)},${r.y.toFixed(0)},${r.w.toFixed(0)}`).join('|');
      if (key !== last) {
        last = key;
        setRings(next);
      }
      setDim(step.dim === true && next.length > 0 && performance.now() - startedRef.current < DIM_MS);
    };
    tick();
    // ~15 Hz is enough to follow pans and sheets, and costs nothing when the tutorial is over.
    const timer = setInterval(tick, TRACK_MS);
    return () => {
      clearInterval(timer);
    };
  }, [game, step, paused, dragging]);

  // Rings from an earlier step are refreshed by the first frame of the next one.
  if (!step || paused || dragging || rings.length === 0) return null;
  const hole = rings[0];
  return (
    <div className={styles.layer} aria-hidden="true" data-testid="coach-spotlight">
      {dim && hole && (
        <svg className={styles.dim} width="100%" height="100%">
          <defs>
            <mask id="coach-hole">
              <rect width="100%" height="100%" fill="white" />
              <ellipse
                cx={hole.x + hole.w / 2}
                cy={hole.y + hole.h / 2}
                rx={hole.w / 2 + 8}
                ry={hole.h / 2 + 8}
                fill="black"
              />
            </mask>
          </defs>
          <rect width="100%" height="100%" mask="url(#coach-hole)" />
        </svg>
      )}
      {rings.map((r) => (
        <span
          key={`${String(Math.round(r.x))},${String(Math.round(r.y))}`}
          className={`${styles.ring} ${r.round ? styles.round : ''}`}
          style={{ left: r.x, top: r.y, width: r.w, height: r.h }}
        />
      ))}
    </div>
  );
}
